import { getAccessTokenCookie } from '@danone-global/internal/graphql'
import FormattedHtmlMessage from '@danone-global/internal/react/components/formatted-html-message'
import {
  Button,
  Grid,
  Loader,
  Notification,
  Typography,
} from '@danone-global/internal/react/components/ui'
import { analytics, Theme, useCore } from '@danone-global/internal/react/core'
import { localisedProductName } from '@danone-global/internal/react/core'
import { makeStyles } from '@material-ui/styles'
import * as Sentry from '@sentry/react'
import clsx from 'clsx'
import React, { useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Select from 'react-select'

import CreateReview from './create-review'
import { useProductReviews } from './reviews.hooks'
import ReviewsList from './reviews-list'

export interface Props {
  sku: string
  analytics?: boolean
  structuredData?: boolean | string
  withSorting?: boolean
  noSwitchPage?: boolean
  reviewLayout?: string
  starClasses?: string
  starActiveClasses?: string
}

const PAGE_SIZE = 3

export const Reviews: React.FC<Props> = ({
  sku,
  analytics: withAnalytics = true,
  structuredData: provideStructuredData = true,
  withSorting = true,
  noSwitchPage = false,
  reviewLayout = undefined,
  starClasses = '',
  starActiveClasses = '',
}) => {
  const [addingReview, setAddingReview] = useState(false)
  const [sorting, setSorting] = useState('createdAt desc')
  const [limit, setLimit] = useState(PAGE_SIZE)
  const [creatingReview, setCreatingReview] = useState(false)
  const [showThankYouMessage, setShowThankYouMessage] = useState(false)
  const [showDuplicateMessage, setShowDuplicateMessage] = useState(false)
  const [selectedOption, setSelectedOption] = useState('none')
  const { reviews, product, refetch, loading } = useProductReviews(
    sku,
    limit,
    sorting,
  )
  const { apiUrl, config, localeConfig } = useCore()
  const productName = localisedProductName(
    product?.masterData?.current,
    localeConfig.locale,
  )
  const classes = useStyles()
  const intl = useIntl()
  const reviewsListRef = useRef(null)

  const options = [
    {
      value: 'createdAt asc',
      label: intl.formatMessage({
        id: 'widgets.reviews.sort.created-at-asc',
        defaultMessage: 'Date Ascending',
      }),
    },
    {
      value: 'createdAt desc',
      label: intl.formatMessage({
        id: 'widgets.reviews.sort.created-at-desc',
        defaultMessage: 'Date Descending',
      }),
    },
    {
      value: 'rating asc',
      label: intl.formatMessage({
        id: 'widgets.reviews.sort.rating-asc',
        defaultMessage: 'Rating Ascending',
      }),
    },
    {
      value: 'rating desc',
      label: intl.formatMessage({
        id: 'widgets.reviews.sort.rating-desc',
        defaultMessage: 'Rating Descending',
      }),
    },
    {
      value: 'custom.fields.helpfulAmount desc',
      label: intl.formatMessage({
        id: 'widgets.reviews.sort.most-helpful',
        defaultMessage: 'Most helpful',
      }),
    },
  ]

  const handleChangeSort = ({ value }) => {
    setSelectedOption(value)
    setSorting(value)
  }

  const toggleCreateNew = () => {
    setAddingReview((adding) => !adding)
    setShowThankYouMessage(false)
  }

  const loadMoreReviews = () => {
    setLimit((existingLimit) => (existingLimit += PAGE_SIZE))
  }

  const handleOnSubmit = async (values) => {
    if (values.email) {
      // Show add message
      // setShowPublishMessage(true)
      setAddingReview(false)
      // Ignore, it's spam
      return
    }

    const accessToken = getAccessTokenCookie()

    try {
      setCreatingReview(true)

      const orderId = new URLSearchParams(window.location.search).get(
        'order_id',
      )

      const response = await fetch(`${apiUrl}/review_commercetools/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rating: values.rating,
          authorName: values.name,
          text: values.comment,
          childAge: values.age,
          title: values.title,
          anonymous: values.submitAnonymous || false,
          helpfulAmount: 0,
          unhelpfulAmount: 0,
          locale: localeConfig.locale,
          productId: product.id,
          clientAccessToken: accessToken,
          uniquenessValue: `${values.userEmail}:${product.id}`,
          storeKey: config.ctStoreKey,
          orderId,
        }),
      })

      const payload = await response.json()

      if (payload.message && payload.message === 'DuplicateField') {
        alert(
          intl.formatMessage({
            id: 'widgets.reviews.alert.only-one-review-allowed',
            defaultMessage: 'Only one review per product is allowed',
          }),
        )
      } else if (
        response.status !== 200 &&
        payload.message === 'Failed to create review'
      ) {
        setShowDuplicateMessage(true)
        setAddingReview(true)
        setShowThankYouMessage(false)
      } else if (response.status !== 200) {
        alert(
          intl.formatMessage({
            id: 'fields.error.please-try-again',
            defaultMessage: 'An error occurred, please try again.',
          }),
        )
      } else {
        setAddingReview(false)
        setShowThankYouMessage(true)

        if (withAnalytics) {
          analytics.push({
            action: 'ADD_REVIEW',
            event: 'add_review',
            ecommerce: {
              item_name: productName,
              item_id: sku,
              rating: values.rating,
            }
          })
        }

        setTimeout(() => {
          reviewsListRef.current?.scrollIntoView({ behavior: 'smooth' })
        }, 200)
      }
    } catch {
      alert(
        intl.formatMessage({
          id: 'global.try-again',
          defaultMessage: 'Please try again',
        }),
      )
    }

    setCreatingReview(false)
  }

  // Loading, or product can't be found
  if (loading || !product) {
    return (
      <div className={classes.root} data-testid={`widget-reviews-${sku}`}>
        <Typography variant="h2">
          <FormattedMessage
            defaultMessage="Reviews"
            id="widgets.reviews.header"
          />
        </Typography>

        <Loader color="dark" variant="small" />
      </div>
    )
  }

  // List of reviews & Add review
  return (
    <>
      {(!addingReview || noSwitchPage) && (
        <div
          ref={reviewsListRef}
          className={classes.root}
          data-testid={`widget-reviews-${sku}`}
        >
          <Typography variant="h2">
            <FormattedMessage
              defaultMessage="Reviews"
              id="widgets.reviews.header"
            />
          </Typography>

          {showThankYouMessage ? (
            <Notification
              message={intl.formatMessage({
                id: 'widgets.reviews.notification.thank-you',
                defaultMessage:
                  "Thank you for your review. Once it's reviewed it will be visible.",
              })}
              variant="global"
            />
          ) : undefined}

          {reviews?.total < 1 ? (
            <Typography className={classes.beFirst}>
              <FormattedMessage
                defaultMessage="Be the first to write a review"
                id="widgets.reviews.notification.be-the-first"
              />
            </Typography>
          ) : undefined}

          <div className={clsx('dtc-actions', classes.actions)}>
            {!addingReview && (
              <Button
                className={classes.addReviewButton}
                data-testid="btn.add-review"
                onClick={toggleCreateNew}
                variant="brand"
              >
                <FormattedMessage
                  defaultMessage="Write a review"
                  id="btn.reviews.new"
                />
              </Button>
            )}

            {withSorting && reviews?.total > 1 ? (
              <div className={classes.sorting}>
                <Select
                  classNamePrefix="reviews-sorting"
                  customStyles={classes.sortSelect}
                  isSearchable={false}
                  name="reviews-select"
                  onChange={handleChangeSort}
                  options={options}
                  placeholder={intl.formatMessage({
                    id: 'widgets.reviews.sort-label',
                    defaultMessage: 'Sort',
                  })}
                  value={options.find(
                    (option) => option.value === selectedOption,
                  )}
                />
              </div>
            ) : undefined}
          </div>

          {[
            'dach-am',
            'dach-loprofin',
            'dach-milupa',
            'dach-nutricia',
          ].includes(config.siteSlug) ? (
            <Typography className={classes.credibility}>
              <FormattedHtmlMessage
                message={intl.formatMessage({
                  id: 'widgets.reviews.notification.credibility-check',
                  defaultMessage:
                    'In order to ensure that the ratings really come from consumers who have actually used or purchased the product, a credibility check is carried out using various (technical) measures.',
                })}
              />
            </Typography>
          ) : undefined}

          <ReviewsList
            productName={productName}
            provideStructuredData={provideStructuredData === true}
            refetch={refetch}
            reviews={reviews}
            sku={sku}
            starActiveClasses={starActiveClasses}
            starClasses={starClasses}
            withAnalytics={withAnalytics}
            {...(reviewLayout && { reviewLayout })}
          />

          {reviews?.count < reviews?.total ? (
            <Grid justify="center" container>
              <Button
                className={classes.moreReviewsButton}
                data-testid="btn.reviews.load-more"
                onClick={loadMoreReviews}
                variant="default"
              >
                <FormattedMessage
                  defaultMessage="More reviews"
                  id="btn.reviews.more"
                />
              </Button>
            </Grid>
          ) : undefined}
        </div>
      )}
      {addingReview && (
        <div
          className={clsx('dtc-add-review', classes.root)}
          data-testid={`widget-reviews-${sku}`}
        >
          {showDuplicateMessage ? (
            <Notification
              message={intl.formatMessage({
                id: 'widgets.reviews.notification.duplicate-review',
                defaultMessage:
                  'You already submitted a review for this product.',
              })}
              variant="global"
            />
          ) : undefined}
          <CreateReview
            creating={creatingReview}
            onSubmit={handleOnSubmit}
            product={product}
            productName={productName}
            showBackButton={!noSwitchPage}
            starActiveClasses={starActiveClasses}
            starClasses={starClasses}
            toggleCreateNew={toggleCreateNew}
          />
        </div>
      )}
    </>
  )
}

export const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      [theme.breakpoints.up('lg')]: {
        margin: '0 auto',
        maxWidth: 750,
      },

      [theme.breakpoints.down('lg')]: {
        margin: theme.spacing(),
      },
    },

    actions: {
      [theme.breakpoints.up('lg')]: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
      },
    },

    sorting: {
      marginBottom: theme.spacing(3),
      fontFamily: theme.typography.fontFamily,
      fontWeight: theme.typography.fontWeightRegular,

      '& .reviews-sorting': {
        [theme.breakpoints.up('lg')]: {
          '&__placeholder, &__single-value': {
            position: 'relative',
            transform: 'none',
            maxWidth: 'none',
            top: 0,
          },
        },

        '&__control': {
          borderRadius: theme.utils.getSize(50),
          boxShadow: 'none',
          borderColor: '#dddddd',
          minHeight: 40,

          '&:hover, :active': {
            borderColor: '#dddddd',
          },
        },

        '&__value-container': {
          padding: theme.spacing(1, 3),
          paddingLeft: theme.spacing(2),
        },

        '&__indicator': {
          marginRight: theme.spacing(),

          '& > svg': {
            color: theme.palette.common.black,
            fill: theme.palette.common.black,
          },

          '&-separator': {
            display: 'none',
          },
        },

        '&__option': {
          '&, &--is-focused': {
            backgroundColor: 'white',
            color: theme.palette.text.primary,
          },
          '&:hover': {
            backgroundColor: theme.utils.hexToRgba(
              theme.palette.primary.main,
              0.1,
            ),
          },
          '&--is-selected, &--is-selected:hover': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
          },
        },
      },

      [theme.breakpoints.up('lg')]: {
        flex: '0 0 auto',
      },
    },

    sortSelect: {},

    beFirst: {
      marginBottom: theme.spacing(3),

      [theme.breakpoints.up('lg')]: {
        marginBottom: theme.spacing(4),
      },
    },

    addReviewButton: {
      marginBottom: 20,

      [theme.breakpoints.down('lg')]: {
        width: '100%',
        justifyContent: 'center',
      },
    },

    moreReviewsButton: {
      width: '100%',
      justifyContent: 'center',
      marginTop: theme.spacing(2),

      [theme.breakpoints.up('lg')]: {
        marginTop: theme.spacing(4),
      },
    },

    credibility: {
      padding: theme.spacing(2),
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: theme.palette.primary.main,
      borderRadius: theme.shape.borderRadius(1),
      margin: theme.spacing(0, 0, 2),

      '& a': {
        color: 'inherit',
      },
      '& a:hover': {
        textDecoration: 'none',
      },
    },
  }),
  { name: 'WidgetReviews' },
)

export default Sentry.withProfiler(Reviews, { name: 'WidgetReviews' })
